// A big map on the board (issue 1172): which pyramid tiles to fetch, keep and let go for a view. Pure, so the rules
// are graded without a browser; `BoardCanvas` runs it whenever the view moves or a tile arrives.
//
// - The level is the one matching the zoom (`pyramidLevelFor`): no texture is ever larger than a 512-pixel tile.
// - Only the tiles touching the view, plus a quarter of a screen around it, are fetched; nearest the middle first.
// - The coarsest level's one tile is always held: something of the map is on the board from the first moment, and a
//   lower level shows under any tile still loading.
// - A tile of another level stays only while it is on screen and something of the right level over it has not arrived.
// - A tile of the right level is let go once it is a whole screen beyond the view: GPU memory stays bounded by the
//   screen's size, never the map's.
import { pyramidBoardRect, pyramidKey, pyramidLevelFor, pyramidTilesIn, parsePyramidKey, type BoardRect, type PyramidLevel } from '@dragons-table/vtt-shared';

export const FETCH_MARGIN = 0.25;
export const KEEP_MARGIN = 1;

export interface PyramidPlan {
  level: number;
  /** Tiles to ask for now, nearest the view's middle first. */
  fetch: string[];
  /** Tiles held that must be let go. */
  drop: string[];
}

const grow = (r: BoardRect, by: number): BoardRect => ({ x: r.x - r.width * by, y: r.y - r.height * by, width: r.width * (1 + 2 * by), height: r.height * (1 + 2 * by) });
const meets = (a: BoardRect, b: BoardRect): boolean => a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;

/**
 * The plan for `view` (the board rectangle on screen) at `zoom` (screen pixels per board pixel, times the device's
 * pixel ratio), given the tiles `held` (arrived) and `asked` (on their way).
 */
export function pyramidPlan(levels: readonly PyramidLevel[], view: BoardRect, zoom: number, held: ReadonlySet<string>, asked: ReadonlySet<string> = new Set()): PyramidPlan {
  const level = pyramidLevelFor(levels, zoom);
  const top = levels[levels.length - 1]!;
  const rectOf = (key: string): BoardRect | null => {
    const p = parsePyramidKey(key);
    const l = p ? levels[p.k] : undefined;
    return p && l ? pyramidBoardRect(l, p.x, p.y) : null;
  };
  const cx = view.x + view.width / 2; const cy = view.y + view.height / 2;
  const want = pyramidTilesIn(level, grow(view, FETCH_MARGIN))
    .map((p) => { const r = pyramidBoardRect(level, p.x, p.y)!; return { key: pyramidKey(level.k, p.x, p.y), d: Math.hypot(r.x + r.width / 2 - cx, r.y + r.height / 2 - cy) }; })
    .sort((a, b) => a.d - b.d).map((w) => w.key);
  const base = pyramidKey(top.k, 0, 0);
  const fetch = [base, ...want].filter((k, i, all) => all.indexOf(k) === i && !held.has(k) && !asked.has(k));
  const keepRect = grow(view, KEEP_MARGIN);
  const drop: string[] = [];
  for (const key of held) {
    if (key === base) continue;
    const p = parsePyramidKey(key); const r = rectOf(key);
    if (!p || !r) { drop.push(key); continue; }
    if (p.k === level.k) { if (!meets(r, keepRect)) drop.push(key); continue; }
    // Another level: kept only while on screen under a tile of the right level that has not arrived yet.
    const covering = pyramidTilesIn(level, r).filter((q) => { const qr = pyramidBoardRect(level, q.x, q.y)!; return meets(qr, view) && meets(qr, r); });
    const needed = meets(r, view) && covering.some((q) => !held.has(pyramidKey(level.k, q.x, q.y)));
    if (!needed) drop.push(key);
  }
  return { level: level.k, fetch, drop };
}
